import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import type {
  CreateSequenceBatchRequest,
  SimulationResult,
  ScenarioBase,
  SimMachineEntry,
  FeasibilityResponse,
  AutoBatchResponse,
  AutoBatchListItem,
  CreateAutoBatchRequest,
  ListScenariosResponse,
  PreviewResponse,
  SimulationPeriod,
} from "@opus-dt/shared-types";
import type { CompareResult } from "../../types/simulations.types.js";

const QK = ["simulations"] as const;

// Preview leve SÍNCRONO e EFÊMERO: espelho do servidor com MTBF/MTTR
// re-derivados na janela de calibração. Keyed por período (inclui calibrationDays) — trocar a
// janela dispara recompute; `keepPreviousData` evita flicker enquanto recalcula ("recalculando").
export function usePreviewScenario(
  scenarioId: string | undefined,
  period: SimulationPeriod,
  enabled: boolean
) {
  const key = `${period.mode}|${period.periodStart ?? ""}|${period.periodEnd ?? ""}|${period.sourceProductionOrderId ?? ""}|${period.calibrationDays}`;
  return useQuery<PreviewResponse>({
    queryKey: ["scenarios", scenarioId, "preview", key],
    queryFn: () => api.post<PreviewResponse>(`/api/v1/scenarios/${scenarioId}/preview`, { period }),
    enabled: enabled && !!scenarioId,
    placeholderData: (prev) => prev, // keepPreviousData (v5): mantém o anterior enquanto refaz
    staleTime: 0,
  });
}

export function useSimulations(refetchInterval = 5_000) {
  return useQuery<SimulationResult[]>({
    queryKey: QK,
    queryFn: () => api.get<SimulationResult[]>("/api/v1/simulations"),
    refetchInterval,
  });
}

export function useSimulation(id: string, refetchInterval?: number) {
  return useQuery<SimulationResult>({
    queryKey: ["simulations", id],
    queryFn: () => api.get<SimulationResult>(`/api/v1/simulations/${id}`),
    refetchInterval,
    enabled: !!id,
  });
}

// Cenários salvos da org para o hub.
export function useScenarios() {
  return useQuery<ListScenariosResponse>({
    queryKey: ["scenarios"],
    queryFn: () => api.get<ListScenariosResponse>("/api/v1/scenarios"),
  });
}

// Autoria de cenário manual.
export function useCreateScenario() {
  const qc = useQueryClient();
  return useMutation({
    // LineId opcional: ausente ⇒ "linha nova" (cenário só do acervo).
    // `productId` faz o cenário nascer com estrutura (executável de imediato).
    mutationFn: (input: {
      lineId?: string;
      base: ScenarioBase;
      name: string;
      notes?: string;
      productId?: string;
    }) => api.post<{ id: string }>("/api/v1/scenarios", input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["scenarios"] }),
    // Consumidor `sandbox/hooks/useStartSandbox.ts` já trata localmente.
    meta: { skipErrorToast: true },
  });
}

export function useSetScenarioSimMachines() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ scenarioId, machines }: { scenarioId: string; machines: SimMachineEntry[] }) =>
      api.put<{ id: string }>(`/api/v1/scenarios/${scenarioId}/sim-machines`, { machines }),
    onSuccess: (_data, { scenarioId }) =>
      qc.invalidateQueries({ queryKey: ["scenarios", scenarioId] }),
  });
}

// Snapshot do cenário (para ler o candidato como linha).
interface ScenarioSnapshot {
  id: string;
  lineId: string;
  machineOverrides: Array<{
    machineId: string;
    simOverrides?: Array<{ param: string; deltaPct: number }>;
  }>;
}

export function useScenario(scenarioId: string) {
  return useQuery<ScenarioSnapshot>({
    queryKey: ["scenarios", scenarioId],
    queryFn: () => api.get<ScenarioSnapshot>(`/api/v1/scenarios/${scenarioId}`),
    enabled: !!scenarioId,
  });
}

// Gate de viabilidade ao vivo.
export function useLineFeasibility(lineId: string, productId: string) {
  return useQuery<FeasibilityResponse>({
    queryKey: ["feasibility", lineId, productId],
    queryFn: () =>
      api.get<FeasibilityResponse>(`/api/v1/lines/${lineId}/feasibility?productId=${productId}`),
    enabled: !!lineId && !!productId,
  });
}

export type { CompareResult } from "../../types/simulations.types.js";

export function useCompareSimulations(a: string | null, b: string | null) {
  return useQuery<CompareResult>({
    queryKey: ["simulations", "compare", a, b],
    queryFn: () => api.get<CompareResult>(`/api/v1/simulations/compare?a=${a}&b=${b}`),
    enabled: !!a && !!b,
  });
}

// ── Simulação Automática (lote) ───────────────────────────────────────────────

const ABQK = ["auto-batches"] as const;

export function useAutoBatches() {
  return useQuery<AutoBatchListItem[]>({
    queryKey: ABQK,
    queryFn: () => api.get<AutoBatchListItem[]>("/api/v1/auto-simulations"),
    // Só faz polling enquanto houver lote ativo; lista parada não bate na API à toa.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((b) => b.status === "QUEUED" || b.status === "RUNNING")
        ? 5_000
        : false,
  });
}

export function useAutoBatch(id: string, refetchInterval?: number) {
  return useQuery<AutoBatchResponse>({
    queryKey: ["auto-batches", id],
    queryFn: () => api.get<AutoBatchResponse>(`/api/v1/auto-simulations/${id}`),
    enabled: !!id,
    // Polling só enquanto o lote está ativo — em terminal (COMPLETED/NO_GAIN/FAILED/…) para
    // sozinho, espelhando o useAutoBatches acima.
    refetchInterval: (query) => {
      if (!refetchInterval) return false;
      const s = query.state.data?.status;
      return s === undefined || s === "QUEUED" || s === "RUNNING" ? refetchInterval : false;
    },
  });
}

export function useCreateAutoBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAutoBatchRequest) =>
      api.post<{ id: string }>("/api/v1/auto-simulations", input),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: ABQK });
      void qc.invalidateQueries({ queryKey: QK });
      // Lote sobre cenário: o prefixo cobre ["scenarios"] (hub) e ["scenarios",id].
      if (variables.scenarioId) void qc.invalidateQueries({ queryKey: ["scenarios"] });
    },
    // Os dois call sites (`SuggestDrawer.tsx`, `AutoConfigForm.tsx`) já tratam o erro.
    meta: { skipErrorToast: true },
  });
}

export function useCancelAutoBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ id: string }>(`/api/v1/auto-simulations/${id}/cancel`, {}),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: ["auto-batches", id] });
      void qc.invalidateQueries({ queryKey: ABQK });
    },
    // Consumidor (`AutoRunProgress.tsx`) chama `.mutate()` sem `onError`; herda o toast.
    meta: { errorTitle: "Falha ao cancelar busca" },
  });
}

export function useSetSimilarMachine(lineId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      machineId,
      similarMachineId,
    }: {
      machineId: string;
      similarMachineId: string | null;
    }) =>
      api.put<{ id: string }>(`/api/v1/machines/${machineId}/sim-similar`, { similarMachineId }),
    onSuccess: (_data, { machineId }) => {
      // useLineMachines usa a chave ["lines", lineId, "machines"] (não ["machines", lineId]).
      void qc.invalidateQueries({ queryKey: ["lines", lineId, "machines"] });
      void qc.invalidateQueries({ queryKey: ["machine", machineId] });
    },
    // Consumidor (`SimilarMachineModal.tsx`) não trata erro hoje; herda o toast.
    meta: { errorTitle: "Falha ao salvar semelhança" },
  });
}

// Lote de SEQUÊNCIA da fila de uma linha. A resposta é o mesmo
// `SimulationBatch` do lote automático, então o acompanhamento reusa `useAutoBatch`
// (progresso/polling) — só o disparo é próprio.
export function useCreateSequenceBatch(lineId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSequenceBatchRequest) =>
      api.post<{ id: string }>(`/api/v1/lines/${lineId}/sequence-batches`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ABQK });
      void qc.invalidateQueries({ queryKey: QK });
    },
    // O drawer ramifica por `error.code` (fila curta / produto incompatível) — trata local.
    meta: { skipErrorToast: true },
  });
}
